import React from "react";
import { CONTEXT_MENU_ACTIONS } from "components/TemplateEditor/Constants";
import { $Popconfirm, $Dropdown, $Menu } from "us.common/components";
import {
  CopyOutlined,
  DeleteOutlined,
  FileOutlined,
  LockOutlined,
  ScissorOutlined,
  UnlockOutlined,
} from "us.icons";
import { useTranslation } from "react-i18next";
import {
  getLockStatus,
  removeSectionFromTemplate,
} from "components/TemplateEditor/Functions";
import { connect } from "react-redux";
import * as Actions from "actions";
import _ from "lodash";
import { useClipboard } from "./Components";
import "./ContextMenu.scss";
import { DEFAULT_SECTION_DATA } from "constants/TemplateEditor";
import { getSectionByName } from "functions";

/**
 * @description Right click menu for the editor
 */
const ContextMenu = (props) => {
  const {
    template,
    section,
    editorSettings,
    children,
    setSettings,
    updateTemplate,
    pasteEventData,
    onPaste,
    onRemoveSection,
    selectSection,
  } = props;

  const { t } = useTranslation();

  const { id, name } = section ?? {};
  const { templateContent } = template.data ?? {};
  const [isClipboardEmpty] = useClipboard();

  /**
   * @description Handle remove section from the template
   */
  const onRemove = (sectionName) => {
    const updatedPages = removeSectionFromTemplate(
      sectionName,
      templateContent?.pages
    );
    const newTemplate = {
      ...template.data,
      templateContent: {
        ...templateContent,
        pages: updatedPages,
      },
    };
    updateTemplate && updateTemplate(newTemplate);
    selectSection && selectSection(DEFAULT_SECTION_DATA);
    onRemoveSection();
  };

  /**
   * @description Handles keyboard shortcuts
   */
  const handleEditorShortCuts = async (e) => {
    //Copy ctrl + C
    if (e.ctrlKey && e.keyCode === 67) {
      copyContent(CONTEXT_MENU_ACTIONS.COPY);
    }
    //Cut ctrl + X
    if (e.ctrlKey && e.keyCode === 88) {
      if (!getLockStatus(id)) {
        await copyContent(CONTEXT_MENU_ACTIONS.CUT);
        onRemove(name);
      }
    }
    //Paste ctrl + V
    if (e.ctrlKey && e.keyCode === 86) {
      pasteContent();
    }
    //Lock alt + L
    if (e.altKey && e.keyCode === 76) {
      handleSectionLock();
    }
    //Delete alt + D
    if (e.altKey && e.keyCode === 68) {
      e.preventDefault();
      !getLockStatus(id) && onRemove(name);
    }
  };

  /**
   * @description - Copy selected content
   */
  const copyContent = async (type) => {
    try {
      const section = getSectionByName(name, templateContent?.pages);
      if (section) {
        const sectionObject = {
          ...section,
          reusable: false,
          type: "usc_section",
          actionType: type,
        };
        await navigator.clipboard.writeText(JSON.stringify(sectionObject));
        setSettings &&
          setSettings({ clipboard: JSON.stringify(sectionObject) });
      }
    } catch (error) {
      console.error("Error copying : ", error);
    }
  };

  /**
   * @description - paste selected content
   */
  const pasteContent = async () => {
    try {
      const permission = await navigator.permissions.query({
        name: "clipboard-read",
      });
      if (permission.state === "denied") {
        throw new Error("Permission denied.");
      }
      const textData = await navigator.clipboard.readText();
      const parsedData = JSON.parse(textData);
      onPaste(parsedData, parsedData?.actionType);
    } catch (e) {
      try {
        if (editorSettings?.clipboard) {
          const parsedData = JSON.parse(editorSettings?.clipboard?.toString());
          onPaste(parsedData, parsedData?.actionType);
        }
      } catch (error) {
        console.error("Error pasting : ", error);
      }
    }
  };

  /**
   * @description - Handle lock functionality
   */
  const handleSectionLock = () => {
    try {
      const element = document.getElementById(id);
      const sectionName = element.getAttribute("sectionname");
      const isLocked = getLockStatus(id);
      if (isLocked) {
        element?.removeAttribute("locked");
      } else {
        element?.setAttribute("locked", "true");
      }

      const pages = templateContent.pages.map((page) => {
        return {
          ...page,
          sections: page.sections.map((section) => {
            if (section.name == sectionName) {
              return { ...section, locked: !isLocked };
            } else {
              return section;
            }
          }),
        };
      });

      const updatedTemplate = {
        ...template.data,
        templateContent: {
          ...templateContent,
          pages,
        },
      };
      updateTemplate && updateTemplate(updatedTemplate);
    } catch (error) {
      console.error("handleSectionLock failed : ", error);
    }
  };

  /**
   * @description - Handle right click menu actions
   * @param action - Menu action
   */
  const handleContextMenuActions = async (action) => {
    switch (action) {
      case CONTEXT_MENU_ACTIONS.COPY:
        copyContent(CONTEXT_MENU_ACTIONS.COPY);
        break;
      case CONTEXT_MENU_ACTIONS.CUT:
        await copyContent(CONTEXT_MENU_ACTIONS.CUT);
        onRemove(name);
        break;
      case CONTEXT_MENU_ACTIONS.PASTE:
        pasteContent();
        break;
      case CONTEXT_MENU_ACTIONS.LOCK:
        handleSectionLock();
        break;
      default:
        break;
    }
  };

  const contextMenu = (
    <$Menu
      style={{ width: "200px" }}
      onClick={({ key }) => handleContextMenuActions(key)}
    >
      {pasteEventData.target?.localName != "canvas" &&
        pasteEventData.target?.id != "divUSCPageContent" && (
          <>
            {!getLockStatus(id) && (
              <$Menu.Item key={CONTEXT_MENU_ACTIONS.DELETE}>
                <$Popconfirm
                  title={t("US.COMMON:COMMON.SURE_TO_PROCEED_?")}
                  onConfirm={() => onRemove(name)}
                >
                  <div className="d-flex justify-content-between">
                    <span>
                      <DeleteOutlined className="text-error mr-2" />
                      {t("US.COMMON:COMMON.DELETE")}
                    </span>
                    <span className="text-muted font-sm">ALT + D</span>
                  </div>
                </$Popconfirm>
              </$Menu.Item>
            )}
            <$Menu.Item key={CONTEXT_MENU_ACTIONS.LOCK}>
              {getLockStatus(id) && (
                <div className="d-flex justify-content-between">
                  <span>
                    <UnlockOutlined className="mr-2" />
                    {t("US.COMMON:COMMON.UNLOCK")}
                  </span>
                  <span className="text-muted font-sm">ALT + L</span>
                </div>
              )}
              {!getLockStatus(id) && (
                <div className="d-flex justify-content-between">
                  <span>
                    <LockOutlined className="mr-2" />
                    {t("US.COMMON:COMMON.LOCK")}
                  </span>
                  <span className="text-muted font-sm">ALT + L</span>
                </div>
              )}
            </$Menu.Item>
            {!getLockStatus(id) && (
              <>
                <$Menu.Divider />
                <$Menu.Item key={CONTEXT_MENU_ACTIONS.CUT}>
                  <div className="d-flex justify-content-between">
                    <span>
                      <ScissorOutlined className="mr-2" />
                      {t("US.COMMON:COMMON.CUT")}
                    </span>
                    <span className="text-muted font-sm">CTRL + X</span>
                  </div>
                </$Menu.Item>
              </>
            )}
            <$Menu.Item key={CONTEXT_MENU_ACTIONS.COPY}>
              <div className="d-flex justify-content-between">
                <span>
                  <CopyOutlined className="mr-2" />
                  {t("US.COMMON:COMMON.COPY")}
                </span>
                <span className="text-muted font-sm">CTRL + C</span>
              </div>
            </$Menu.Item>
          </>
        )}

      <$Menu.Item key={CONTEXT_MENU_ACTIONS.PASTE} disabled={isClipboardEmpty}>
        <div className="d-flex justify-content-between">
          <span>
            <FileOutlined className="mr-2" />
            {t("US.COMMON:COMMON.PASTE")}
          </span>
          <span className="text-muted font-sm">CTRL + V</span>
        </div>
      </$Menu.Item>
    </$Menu>
  );

  return (
    <$Dropdown
      overlay={contextMenu}
      trigger={["contextMenu"]}
      overlayClassName="custom-menu-item"
    >
      <div tabIndex="0" onKeyDown={handleEditorShortCuts}>
        {children}
      </div>
    </$Dropdown>
  );
};

const mapStateToProps = (state) => {
  const { templateEditor, templatesHistory } = state;
  const { section, editorSettings } = templateEditor;
  const { template } = templatesHistory;

  return {
    template,
    section,
    editorSettings,
  };
};

const { editorSettings, section } = Actions.templateEditor;
const { template } = Actions.templatesHistory;

const mapDispatchToProps = {
  setSettings: editorSettings.set,
  updateTemplate: template.update,
  selectSection: section.selectSection,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(ContextMenu);
